import React, { useCallback } from 'react';
import { Button, Form, Input as FormInput, Select, DatePicker, Space, Typography, Upload, ConfigProvider, Row, Col, Card } from 'antd';
import { useNavigate } from 'react-router-dom';
import { UploadOutlined } from '@ant-design/icons';
import { themeConfig } from '../theme/colors';
import { addPatient } from '../data/patient';

const { Title } = Typography;
const { Option } = Select;

const AddPatientPage = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const handleSubmit = useCallback(() => {
    form
      .validateFields()
      .then((values) => {
        console.log('Form Values:', values);
        addPatient(values);
        form.resetFields();
        navigate('/patient');
      })
      .catch((info) => {
        console.log('Validation Failed:', info);
      });
  }, [form, navigate]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    navigate('/patient');
  }, [form, navigate]);

  // Add effect to handle keyboard events
  React.useEffect(() => {
    const handleKeyPress = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        handleSubmit();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        handleCancel();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleSubmit, handleCancel]);

  return (
    <ConfigProvider theme={themeConfig}>
      <div 
        style={{ 
          padding: '24px', 
          background: themeConfig.token.colorBgLayout,
          minHeight: 'calc(100vh - 120px)',
        }}
      >
        <Title level={3} style={{ color: themeConfig.token.colorTextHeading, marginBottom: 24 }}>
          Add Patient (Ctrl/⌘ + S to save, Esc to cancel)
        </Title>
        <Form 
          form={form} 
          layout="vertical"
          onKeyPress={(e) => {
            if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
              e.preventDefault();
            }
          }}
        >
          {/* Name Section */}
          <Card title="Name" style={{ marginBottom: 24 }} bordered={false}>
            <Row gutter={[16, 8]} align="middle">
              <Col xs={24} sm={8}>
                <Form.Item
                  name={['name', 'firstName']}
                  label="First Name"
                  rules={[{ required: true, message: 'Please enter first name' }]}
                >
                  <FormInput placeholder="First Name" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name={['name', 'middleName']}
                  label="Middle Name"
                >
                  <FormInput placeholder="Middle Name" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name={['name', 'lastName']}
                  label="Last Name"
                  rules={[{ required: true, message: 'Please enter last name' }]}
                >
                  <FormInput placeholder="Last Name" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Contact Section */}
          <Card title="Contact Information" style={{ marginBottom: 24 }} bordered={false}>
            <Row gutter={[16, 8]} align="middle">
              <Col xs={24} sm={8}>
                <Form.Item
                  name={['contact', 'mobile']}
                  label="Mobile Number"
                  rules={[{ required: true, message: 'Please enter mobile number' }]}
                >
                  <FormInput placeholder="Mobile Number" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name={['contact', 'email']}
                  label="Email"
                  rules={[
                    { message: 'Please enter email' },
                    { type: 'email', message: 'Please enter a valid email' }
                  ]}
                >
                  <FormInput placeholder="Email" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name={['contact', 'alternatePhone']}
                  label="Alternate Phone"
                >
                  <FormInput placeholder="Alternate Phone" />
                </Form.Item>
              </Col>
            </Row>
          </Card>


          {/* Gender Section */}
          <Card title="Gender" style={{ marginBottom: 24 }} bordered={false}>
            <Form.Item name="gender" rules={[{ required: true, message: 'Please select gender' }]}>
              <Select placeholder="Select Gender">
                <Option value="female">Female</Option>
                <Option value="male">Male</Option>
                <Option value="other">Other</Option>
                <Option value="unknown">Unknown</Option>
              </Select>
            </Form.Item>
          </Card>

          {/* Birth Date Section */}
          <Card title="Birth Date" style={{ marginBottom: 24 }} bordered={false}>
            <Form.Item name="birthDate" rules={[{ required: true, message: 'Please select birth date' }]}>
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Card>

          {/* Address Section */}
          <Card title="Address" style={{ marginBottom: 24 }} bordered={false}>
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={4}>
                <Form.Item
                  name={['address', 'use']}
                  rules={[{ required: true, message: 'Please select use' }]}
                >
                  <Select placeholder="Use">
                    <Option value="home">Home</Option>
                    <Option value="work">Work</Option>
                    <Option value="temp">Temp</Option>
                    <Option value="old">Old</Option>
                    <Option value="billing">Billing</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={4}>
                <Form.Item
                  name={['address', 'type']}
                  rules={[{ required: true, message: 'Please select type' }]}
                >
                  <Select placeholder="Type">
                    <Option value="postal">Postal</Option>
                    <Option value="physical">Physical</Option>
                    <Option value="both">Both</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={4}>
                <Form.Item name={['address', 'line1']}>
                  <FormInput placeholder="Line 1" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={4}>
                <Form.Item name={['address', 'line2']}>
                  <FormInput placeholder="Line 2" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={4}>
                <Form.Item name={['address', 'city']}>
                  <FormInput placeholder="City" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={4}>
                <Form.Item name={['address', 'state']}>
                  <FormInput placeholder="State" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={4}>
                <Form.Item name={['address', 'postalCode']}>
                  <FormInput placeholder="Postal Code" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Marital Status Section */}
          <Card title="Marital Status" style={{ marginBottom: 24 }} bordered={false}>
            <Form.Item name="maritalStatus">
              <Select placeholder="Select Marital Status">
                <Option value="annulled">Annulled</Option>
                <Option value="divorced">Divorced</Option>
                <Option value="legally separated">Legally Separated</Option>
                <Option value="married">Married</Option>
                <Option value="polygamous">Polygamous</Option>
                <Option value="never married">Never Married</Option>
                <Option value="domestic partner">Domestic Partner</Option>
                <Option value="unmarried">Unmarried</Option>
                <Option value="widowed">Widowed</Option>
              </Select>
            </Form.Item>
          </Card>

          {/* Photo Section */}
          <Card title="Photo" style={{ marginBottom: 24 }} bordered={false}>
            <Form.Item
              name="photo"
              valuePropName="fileList"
              getValueFromEvent={(e) => (Array.isArray(e) ? e : e && e.fileList)}
            >
              <Upload
                name="photo"
                listType="picture"
                beforeUpload={() => false}
                maxCount={1}
              >
                <Button icon={<UploadOutlined />} style={{ borderRadius: themeConfig.token.borderRadius }}>
                  Upload Photo
                </Button>
              </Upload>
            </Form.Item>
          </Card>

          {/* General Practitioner Section */}
          <Card title="General Practitioner" style={{ marginBottom: 24 }} bordered={false}>
            <Form.Item name={['generalPractitioner', 'organization']}>
              <FormInput placeholder="Organization" />
            </Form.Item>
          </Card>

          {/* Managing Organization Section */}
          <Card title="Managing Organization" style={{ marginBottom: 24 }} bordered={false}>
            <Form.Item name="managingOrganization">
              <FormInput placeholder="Managing Organization" />
            </Form.Item>
          </Card>

          {/* Form Actions */}
          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                onClick={handleSubmit}
                style={{ borderRadius: themeConfig.token.borderRadius }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSubmit();
                  }
                }}
              >
                Create (Ctrl/⌘ + S)
              </Button>
              <Button 
                onClick={handleCancel}
                style={{ borderRadius: themeConfig.token.borderRadius }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCancel();
                  }
                }}
              >
                Cancel (Esc)
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
    </ConfigProvider>
  );
};

export default AddPatientPage;